import { fieldLabelId, RadioButton } from '@lbcamden/lib-react';
import { nanoid } from 'nanoid';
import { PropsWithChildren, ReactNode, useMemo } from 'react';

import {
  ConditionalFormInputRoot,
  NestedContainer,
  RadioButtonContainer,
} from './ConditionalFormInput.styled';

export type ConditionalFormInputProps<TYes> = {
  id?: string;
  className?: string;
  yesLabel?: ReactNode;
  noLabel?: ReactNode;
  value?: false | TYes;
  initialYesValue: TYes;
  disabled?: boolean;
  onChange(value: false | TYes): void;
};

export const ConditionalFormInput = <TYes,>({
  id,
  className,
  yesLabel = 'Yes',
  noLabel = 'No',
  value,
  initialYesValue,
  disabled,
  children,
  onChange,
}: PropsWithChildren<ConditionalFormInputProps<TYes>>) => {
  const rootId = useMemo(() => id || nanoid(), [id]);

  const yesButtonId = `${rootId}-yes`;
  const noButtonId = `${rootId}-no`;

  const isTrue = ![undefined, false].includes(value as never);

  const onClickYes = () => !isTrue && onChange(initialYesValue);

  const onClickNo = () => onChange(false);

  return (
    <ConditionalFormInputRoot
      role="radiogroup"
      aria-labelledby={rootId && fieldLabelId(rootId)}
      id={rootId}
      className={className}
    >
      <RadioButtonContainer>
        <RadioButton
          id={yesButtonId}
          name={rootId}
          checked={isTrue}
          disabled={disabled}
          onChange={onClickYes}
        />
        <label htmlFor={yesButtonId}>{yesLabel}</label>
      </RadioButtonContainer>

      {isTrue && <NestedContainer>{children}</NestedContainer>}

      <RadioButtonContainer>
        <RadioButton
          id={noButtonId}
          name={rootId}
          checked={value === false}
          disabled={disabled}
          onChange={onClickNo}
        />
        <label htmlFor={noButtonId}>{noLabel}</label>
      </RadioButtonContainer>
    </ConditionalFormInputRoot>
  );
};
